import { createTheme } from '@mui/material/styles';

/*
 * @mui/material overrides for the theme extension.
 * @see https://mui.com/material-ui/customization/theming/#custom-variables
 */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import type * as mui from '@mui/material';
import { PaletteColor } from '@mui/material/styles/createPalette'; // eslint-disable-line @typescript-eslint/no-unused-vars

declare module '@mui/material/styles' {
  interface TypographyVariants {
    muted: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    muted?: React.CSSProperties;
  }

  interface CustomPalette {
    white?: mui.PaletteColorOptions;
  }
  interface Palette extends CustomPalette {} // eslint-disable-line @typescript-eslint/no-empty-interface
  interface PaletteOptions extends CustomPalette {} // eslint-disable-line @typescript-eslint/no-empty-interface
}

// Update the Typography's variant prop options
declare module '@mui/material' {
  interface TypographyPropsVariantOverrides {
    muted: true;
  }
  interface ButtonPropsColorOverrides {
    white?: true;
  }
}

const { palette } = createTheme();
const { augmentColor } = palette;
const createColor = (mainColor: string): PaletteColor => augmentColor({ color: { main: mainColor } });

export default createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
        list: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: 8,
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiTypography: {
      styleOverrides: {
        gutterBottom: {
          marginBottom: '0.7rem',
        },
      },
    },
  },
  palette: {
    primary: {
      main: '#7dbbeb',
      contrastText: '#ffffff',
      light: '#c2e1f7',
      dark: '#4f9fe7',
    },
    secondary: {
      main: '#1f74b6',
      light: '#96c8ed',
      dark: '#18598c',
    },
    text: {
      primary: '#323c44',
    },
    success: {
      main: '#00b300',
    },
    error: {
      main: '#fd5200',
    },
    white: createColor('#ffffff'),
  },
  shape: {
    borderRadius: 0,
  },
  typography: {
    htmlFontSize: 16,
    fontFamily: [
      '"Source Sans Pro"',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      '"Helvetica Neue"',
      'sans-serif'].join(','),
    h1: {
      fontSize: '2rem',
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        '"Helvetica Neue"',
        'sans-serif'].join(','),
      fontWeight: 300,
      lineHeight: 1.167,
      letterSpacing: '-0.01562rem',
      margin: '0 0 0.35rem',
    },
    h2: {
      margin: '.5em 0 .4rem',
      fontSize: '1.7rem',
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        '"Helvetica Neue"',
        'sans-serif'].join(','),
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: '-0.00833rem',
    },
    h3: {
      margin: 0,
      fontSize: '1.5rem',
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        '"Helvetica Neue"',
        'sans-serif'].join(','),
      fontWeight: 400,
      lineHeight: 1.167,
      letterSpacing: 0,
    },
    h4: {
      margin: 0,
      fontSize: '1.2rem',
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        '"Helvetica Neue"',
        'sans-serif'].join(','),
      fontWeight: 400,
      lineHeight: 1.235,
      letterSpacing: '0.00735rem',
    },
    h5: {
      margin: 0,
      fontSize: '1rem',
      fontFamily: [
        '"Open Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'Oxygen',
        'Ubuntu',
        'Cantarell',
        '"Helvetica Neue"',
        'sans-serif'].join(','),
      textTransform: 'uppercase',
      fontWeight: 400,
      lineHeight: 1.334,
      letterSpacing: 0,
    },
    h6: {
      margin: 0,
      fontSize: '1rem',
      fontFamily: '"Source Sans Pro","Roboto","Helvetica","Arial",sans-serif',
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: '0.0075rem',
    },
    body1: {
      lineHeight: 1.3,
    },
    muted: {
      margin: 0,
      fontFamily: '"Source Sans Pro","Roboto","Helvetica","Arial",sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03333rem',
      color: '#8e959c',
    },
  },
});
